import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  Checkbox,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Spinner from '../../components/Spinner';
import { makeStyles } from '@mui/styles';
import PageLayout from '../../components/PageLayout';
import ActionFilter from '../../components/ActionFilter';
import AddAudioModal from './AddAudioModal';
import BulkUploadModal from './BulkUploadModal';
import BulkActionsBar from './BulkActionsBar';
import BulkCategoryModal from './BulkCategoryModal';
import BulkContestModal from './BulkContestModal';
import { listCategories } from '../../http/auth';
import ConfirmDeleteModal from '../ManageBadge/ConfirmDeleteModal.jsx';
import { deleteAudio, listAudios, updateAudio } from '../../http/audio';
// CommonPagination replaced with infinite scroll
import ViewAudioModal from './ViewAudiModal';
import { getCategories } from '../../utils/common';
import SearchIcon from '@mui/icons-material/Search';
import defaultAudioThumbnail from '../../assets/default-audio-thumbnail.svg';
import AxiosInstance from '../../services/networkServices';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '90vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    padding: '20px 24px',
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(102, 126, 234, 0.08)',
    border: '1px solid rgba(102, 126, 234, 0.08)',
  },
  dialogContent: {
    maxWidth: '100%',
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: 'auto',
    maxHeight: 'calc(100vh - 135px)',
    minHeight: 'calc(100vh - 135px)',
    boxShadow: 'none !important',
    borderRadius: '12px',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(102, 126, 234, 0.05)',
    },
  },
  table: {
    minWidth: '100%',
    border: 'none',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  stickyHeader: {
    position: 'sticky',
    top: '-1px',
    zIndex: 1,
  },
  actionsCell: {
    textAlign: 'center',
  },
  actionsHeader: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  searchField: {
    width: '300px',
    '& .MuiInputLabel-root': {
      color: '#667eea',
      borderRadius: '15px',
      height: '50px',
    },
    '& .MuiInputBase-formControl': {
      padding: '15px',
      display: 'flex',
      alignItems: 'center',
      '& input': {
        padding: 0,
      },
      '& fieldset': {
        borderColor: 'rgba(102, 126, 234, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: '#667eea',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
  },
  input: {
    marginBottom: '0 !important',
    '& label.Mui-focused': {
      color: '#667eea',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#667eea',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      '& fieldset': {
        borderColor: 'rgba(102, 126, 234, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: '#667eea',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
        boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
      },
    },
  },
  button: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important',
    color: '#fff !important',
    borderRadius: '12px !important',
    fontSize: '14px !important',
    height: '44px !important',
    border: 'none !important',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3) !important',
    transition: 'all 0.3s ease !important',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%) !important',
      boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4) !important',
      transform: 'translateY(-1px)',
    },
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: '10px',
  },
}));

// Color palette for category pills - vibrant modern colors
const CATEGORY_COLORS = [
  { bg: '#667eea', text: 'white' },  // Electric Blue
  { bg: '#764ba2', text: 'white' },  // Vibrant Purple
  { bg: '#ff6b6b', text: 'white' },  // Coral
  { bg: '#20c997', text: 'white' },  // Teal
  { bg: '#ffc107', text: '#1a1a2e' },  // Amber
  { bg: '#00bcd4', text: 'white' },  // Cyan
  { bg: '#e91e63', text: 'white' },  // Pink
  { bg: '#9c27b0', text: 'white' },  // Deep Purple
  { bg: '#009688', text: 'white' },  // Teal Dark
  { bg: '#ff5722', text: 'white' },  // Deep Orange
  { bg: '#3f51b5', text: 'white' },  // Indigo
  { bg: '#8bc34a', text: '#1a1a2e' },  // Light Green
];

// Get consistent color based on category name
const getCategoryColor = (categoryName) => {
  let hash = 0;
  for (let i = 0; i < categoryName.length; i++) {
    hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % CATEGORY_COLORS.length;
  return CATEGORY_COLORS[index];
};

const CategoryPill = ({ category }) => {
  const colors = getCategoryColor(category.name || '');
  const isActive = category.status === 'ACTIVE';
  
  return (
    <Chip
      label={category.name}
      sx={{
        backgroundColor: isActive ? colors.bg : 'white',
        border: isActive ? 'none' : `2px solid ${colors.bg}`,
        color: isActive ? colors.text : colors.bg,
        borderRadius: '20px',
        margin: '3px',
        opacity: isActive ? 1 : 0.8,
        fontWeight: 500,
        boxShadow: isActive ? `0 2px 8px ${colors.bg}40` : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: `0 4px 12px ${colors.bg}50`,
        },
        '& .MuiChip-label': {
          fontSize: '12px',
          padding: '0 14px',
        },
      }}
    />
  );
};

const ContestPill = ({ contest }) => {
  return (
    <Chip
      avatar={<Avatar src={contest.logo} sx={{ border: '2px solid rgba(255,255,255,0.3)' }} />}
      label={`${contest.title.slice(0, 15)}...`}
      size="small"
      sx={{
        background: 'linear-gradient(135deg, #20c997 0%, #17a87d 100%)',
        color: 'white',
        margin: '2px',
        fontWeight: 500,
        boxShadow: '0 2px 8px rgba(32, 201, 151, 0.3)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: '0 4px 12px rgba(32, 201, 151, 0.4)',
        },
        '& .MuiChip-label': {
          fontSize: '12px',
          padding: '12px',
        },
      }}
    />
  );
};

const ChallengePill = ({ challenge }) => {
  return (
    <Chip
      label={`${challenge.title.slice(0, 15)}...`}
      size="small"
      sx={{
        background: 'linear-gradient(135deg, #ff6b6b 0%, #e04848 100%)',
        color: 'white',
        margin: '2px',
        fontWeight: 500,
        boxShadow: '0 2px 8px rgba(255, 107, 107, 0.25)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: '0 4px 12px rgba(255, 107, 107, 0.35)',
        },
        '& .MuiChip-label': {
          fontSize: '12px',
          padding: '12px',
        },
      }}
    />
  );
};

const ManageAudio = () => {
  const classes = useStyles();
  const [allAudios, setAllAudios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [audioModal, setAudioModal] = useState('');
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [selectedAudio, setSelectedAudio] = useState({});
  const [contests, setContests] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedAudioIds, setSelectedAudioIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);
  const searchTimeoutRef = useRef(null);
  const tableContainerRef = useRef(null);
  const ITEMS_PER_LOAD = 20;

  useEffect(() => {
    fetchCategories();
    fetchAudios();
    fetchContests();
    fetchChallenges();

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Memoized filtered results - only recalculates when filters change
  const filteredAudios = useMemo(() => {
    let filtered = allAudios;

    // Apply category filter (multi-select)
    if (selectedCategoryIds.length > 0) {
      filtered = filtered.filter((audio) =>
        audio?.categories?.some(category => selectedCategoryIds.includes(category._id))
      );
    }

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((audio) => {
        // Search in audio title
        if (audio?.name?.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in categories
        if (audio?.categories?.some(category => 
          category?.name?.toLowerCase().includes(query)
        )) {
          return true;
        }
        
        // Search in contests
        if (audio?.contests?.some(contest => 
          contest?.title?.toLowerCase().includes(query)
        )) {
          return true;
        }

        // Search in challenges
        if (audio?.challenges?.some(challenge =>
          challenge?.title?.toLowerCase().includes(query)
        )) {
          return true;
        }
        
        return false;
      });
    }

    return filtered;
  }, [allAudios, searchQuery, selectedCategoryIds]);

  // Memoized results with infinite scroll limit
  const displayedAudios = useMemo(() => {
    return filteredAudios.slice(0, displayLimit);
  }, [filteredAudios, displayLimit]);

  const hasMoreToLoad = displayLimit < filteredAudios.length;

  // Update total count and reset display limit when filters change
  useEffect(() => {
    setTotalCount(filteredAudios.length);
    // Reset display limit when filters change
    setDisplayLimit(ITEMS_PER_LOAD);
  }, [filteredAudios]);

  // Handle infinite scroll
  const handleScroll = () => {
    const container = tableContainerRef.current;
    if (!container || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Load more when scrolled to within 100px of the bottom
    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMoreToLoad) {
      setDisplayLimit((prev) => Math.min(prev + ITEMS_PER_LOAD, filteredAudios.length));
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [hasMoreToLoad, loading, filteredAudios.length]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const payload = { page: 1, limit: 100, type: 'post' };
      const res = await listCategories(payload);
      setCategoryTypes(res?.data?.list || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAudios = async () => {
    try {
      setLoading(true);
      const payload = { page: 1, limit: 500 }; // Fetch all audios for client-side filtering
      const res = await listAudios(payload);
      setAllAudios(res?.data?.list || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchContests = async () => {
    try {
      const response = await AxiosInstance.get('/contest?page=1&limit=500');
      if (response?.data?.status) {
        setContests(response.data.data.list.filter(contest => contest.isActive) || []);
      }
    } catch (error) {
      console.error('Error fetching contests:', error);
    }
  };

  const fetchChallenges = async () => {
    try {
      const response = await AxiosInstance.post('/category/challenge/list', {
        page: 1,
        limit: 500,
        sortBy: 'startDate',
        sortOrder: 1,
      });
      if (response?.data?.status) {
        const now = new Date();
        const assignableChallenges = (response.data.data.list || []).filter(
          (challenge) => new Date(challenge.endDate) >= now
        );
        setChallenges(assignableChallenges);
      }
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  };

  const confirmDeleteHandler = async () => {
    try {
      setLoading(true);
      const payload = { id: selectedAudio?._id };
      await deleteAudio(payload);
      fetchAudios();
      closeModalHandler();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };


  const openAddModalHandler = () => {
    setAudioModal('add');
  };

  const handleViewAudio = (audio) => {
    setSelectedAudio(audio);
    setAudioModal('view');
  };

  const handleEditAudio = (audio) => {
    setSelectedAudio(audio);
    setAudioModal('edit');
  };

  const handleDeleteAudio = (audio) => {
    setSelectedAudio(audio);
    setAudioModal('delete');
  };

  const closeModalHandler = () => {
    setAudioModal('');
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleCategoryFilterChange = (event) => {
    const value = event.target.value;
    setSelectedCategoryIds(typeof value === 'string' ? value.split(',') : value);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategoryIds([]);
  };

  // Multi-select handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = displayedAudios.map((audio) => audio._id);
      setSelectedAudioIds(allIds);
    } else {
      setSelectedAudioIds([]);
    }
  };

  const handleSelectAudio = (audioId) => {
    setSelectedAudioIds((prev) =>
      prev.includes(audioId)
        ? prev.filter((id) => id !== audioId)
        : [...prev, audioId]
    );
  };

  const isAllSelected = displayedAudios.length > 0 && selectedAudioIds.length === displayedAudios.length;
  const isIndeterminate = selectedAudioIds.length > 0 && selectedAudioIds.length < displayedAudios.length;

  const getSelectedAudios = () => {
    return allAudios.filter((audio) => selectedAudioIds.includes(audio._id));
  };

  const handleBulkDelete = async () => {
    setAudioModal('bulkDelete');
  };

  const confirmBulkDeleteHandler = async () => {
    try {
      setLoading(true);
      for (const audioId of selectedAudioIds) {
        await deleteAudio({ id: audioId });
      }
      toast.success(`Successfully deleted ${selectedAudioIds.length} audio(s)`);
      setSelectedAudioIds([]);
      fetchAudios();
      closeModalHandler();
    } catch (err) {
      console.error('Error deleting audios:', err);
      toast.error('Failed to delete some audios');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCategoryAssign = () => {
    setAudioModal('bulkCategory');
  };

  const handleBulkContestAssign = () => {
    setAudioModal('bulkContest');
  };

  const handleBulkCategorySubmit = async (categoryIds, action) => {
    try {
      setLoading(true);
      const selectedAudios = getSelectedAudios();
      
      for (const audio of selectedAudios) {
        const formData = new FormData();
        formData.append('id', audio._id);
        formData.append('name', audio.name);
        
        let newCategories;
        if (action === 'add') {
          // Add new categories to existing ones
          const existingCatIds = audio.categories?.map(c => c._id) || [];
          newCategories = [...new Set([...existingCatIds, ...categoryIds])];
        } else {
          // Remove categories
          const existingCatIds = audio.categories?.map(c => c._id) || [];
          newCategories = existingCatIds.filter(id => !categoryIds.includes(id));
        }
        
        newCategories.forEach((catId) => {
          formData.append('categoryIds[]', catId);
          formData.append(`categoryStatus[${catId}]`, 'ACTIVE');
        });
        
        // Keep existing contests
        audio.contests?.forEach((contest) => {
          formData.append('contestIds[]', contest._id);
        });
        
        await updateAudio(formData);
      }
      
      toast.success(`Successfully updated categories for ${selectedAudios.length} audio(s)`);
      setSelectedAudioIds([]);
      fetchAudios();
      closeModalHandler();
    } catch (err) {
      console.error('Error updating categories:', err);
      toast.error('Failed to update some audios');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkContestSubmit = async (contestIds, action) => {
    try {
      setLoading(true);
      const selectedAudios = getSelectedAudios();
      
      for (const audio of selectedAudios) {
        const formData = new FormData();
        formData.append('id', audio._id);
        formData.append('name', audio.name);
        
        let newContests;
        if (action === 'add') {
          // Add new contests to existing ones
          const existingContestIds = audio.contests?.map(c => c._id) || [];
          newContests = [...new Set([...existingContestIds, ...contestIds])];
        } else {
          // Remove contests
          const existingContestIds = audio.contests?.map(c => c._id) || [];
          newContests = existingContestIds.filter(id => !contestIds.includes(id));
        }
        
        newContests.forEach((contestId) => {
          formData.append('contestIds[]', contestId);
        });
        
        // Keep existing categories
        audio.categories?.forEach((cat) => {
          formData.append('categoryIds[]', cat._id);
          formData.append(`categoryStatus[${cat._id}]`, cat.status || 'ACTIVE');
        });
        
        await updateAudio(formData);
      }
      
      toast.success(`Successfully updated contests for ${selectedAudios.length} audio(s)`);
      setSelectedAudioIds([]);
      fetchAudios();
      closeModalHandler();
    } catch (err) {
      console.error('Error updating contests:', err);
      toast.error('Failed to update some audios');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title="Audio"
      loading={loading}
      actions={
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            sx={{
              padding: {
                xs: '8px 12px',
                sm: '8px 20px',
                md: '8px 20px',
                lg: '10px 24px',
                xl: '10px 24px',
              },
              backgroundColor: 'transparent',
              color: '#667eea',
              border: '2px solid #667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.08) !important',
                borderColor: '#764ba2',
                color: '#764ba2',
                transform: 'translateY(-1px)',
              },
              borderRadius: '12px',
              fontSize: {
                xs: '12px',
                sm: '13px',
                md: '13px',
                lg: '14px',
                xl: '14px',
              },
              fontWeight: 600,
              height: {
                xs: '38px',
                sm: '44px',
                md: '44px',
                lg: '44px',
                xl: '44px',
              },
              transition: 'all 0.3s ease',
            }}
            variant="outlined"
            onClick={() => setAudioModal('bulk')}
          >
            Bulk Upload
          </Button>
          <Button
            sx={{
              padding: {
                xs: '8px 12px',
                sm: '8px 20px',
                md: '8px 20px',
                lg: '10px 24px',
                xl: '10px 24px',
              },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4292 100%) !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
              },
              borderRadius: '12px',
              fontSize: {
                xs: '12px',
                sm: '13px',
                md: '13px',
                lg: '14px',
                xl: '14px',
              },
              fontWeight: 600,
              height: {
                xs: '38px',
                sm: '44px',
                md: '44px',
                lg: '44px',
                xl: '44px',
              },
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              transition: 'all 0.3s ease',
            }}
            variant="contained"
            onClick={openAddModalHandler}
          >
            Upload Audio
          </Button>
        </Box>
      }
    >
      <>
        {/* Filters and Search Bar */}
        <Box sx={{ marginBottom: '20px', width: '100%' }}>
          <Grid container spacing={2} alignItems="stretch" sx={{ width: '100%' }}>
            <Grid item xs={12} sm={4} md={3}>
              <FormControl fullWidth size="small" sx={{ height: '100%' }}>
                <InputLabel>Filter by Category</InputLabel>
                <Select
                  multiple
                  value={selectedCategoryIds}
                  label="Filter by Category"
                  onChange={handleCategoryFilterChange}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const category = categoryTypes.find(cat => cat._id === value);
                        return (
                          <Chip
                            key={`selected-${category?._id}`}
                            label={category?.name || value}
                            size="small"
                            sx={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 500,
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#fff',
                      height: '44px',
                      '& fieldset': {
                        borderColor: 'rgba(102, 126, 234, 0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                        boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                      },
                    },
                    '& .MuiSelect-select': {
                      color: '#1a1a2e',
                    },
                  }}
                >
                  {categoryTypes.map((category) => (
                    <MenuItem key={`menu-item-${category._id}`} value={category._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <input
                          type="checkbox"
                          checked={selectedCategoryIds.includes(category._id)}
                          readOnly
                          style={{
                            marginRight: '8px',
                            accentColor: '#142C5C',
                          }}
                        />
                        <span style={{ color: '#000' }}>{category.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={5} md={7}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by audio title, category, contest, or challenge..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    height: '44px',
                    transition: 'all 0.2s ease',
                    '& fieldset': {
                      borderColor: 'rgba(102, 126, 234, 0.3)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                      boxShadow: '0 0 0 4px rgba(102, 126, 234, 0.1)',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 8px',
                    fontSize: '14px',
                    color: '#1a1a2e',
                    '&::placeholder': {
                      color: '#94a3b8',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={3} md={2}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                sx={{
                  height: '44px',
                  borderColor: '#ff6b6b',
                  color: '#ff6b6b',
                  borderRadius: '12px',
                  padding: '0 20px',
                  minWidth: '80px',
                  fontWeight: 600,
                  border: '2px solid #ff6b6b',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#ff6b6b',
                    backgroundColor: '#ff6b6b',
                    color: '#ffffff',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Audio count display - moved to top */}
        <Box sx={{ 
          padding: '12px 16px', 
          marginBottom: '12px',
          textAlign: 'left',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
        }}>
          <Typography sx={{ 
            fontSize: '14px', 
            color: '#64748B',
            fontWeight: 500,
          }}>
            Showing <span style={{ color: '#667eea', fontWeight: 600 }}>{displayedAudios.length}</span> of <span style={{ color: '#667eea', fontWeight: 600 }}>{totalCount}</span> audios
            {hasMoreToLoad && <span style={{ color: '#764ba2' }}> • Scroll down for more</span>}
          </Typography>
        </Box>

        <Box
          sx={{
            padding: '0',
            borderRadius: '16px',
            boxShadow: '0 4px 24px rgba(102, 126, 234, 0.12)',
            border: '1px solid rgba(102, 126, 234, 0.08)',
            background: '#ffffff',
            overflow: 'hidden',
          }}
        >
          <TableContainer component={Paper} className={classes.tableContainer} ref={tableContainerRef}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow className={classes.stickyHeader}>
                  <TableCell
                    sx={{
                      width: '50px',
                      padding: '10px',
                      background: '#f8f9fa',
                    }}
                  >
                    <Checkbox
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={handleSelectAll}
                      sx={{
                        color: '#667eea',
                        '&.Mui-checked': { color: '#667eea' },
                        '&.MuiCheckbox-indeterminate': { color: '#667eea' },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: '13px',
                        sm: '14px',
                        md: '14px',
                        lg: '15px',
                        xl: '15px',
                      },
                      color: '#1a1a2e',
                      fontWeight: 600,
                      padding: {
                        xs: '12px 8px',
                        sm: '16px 12px',
                        md: '16px 12px',
                        lg: '18px 16px',
                        xl: '18px 16px',
                      },
                      background: '#f8f9fa',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Thumbnail
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: '13px',
                        sm: '14px',
                        md: '14px',
                        lg: '15px',
                        xl: '15px',
                      },
                      color: '#1a1a2e',
                      fontWeight: 600,
                      padding: {
                        xs: '12px 8px',
                        sm: '16px 12px',
                        md: '16px 12px',
                        lg: '18px 16px',
                        xl: '18px 16px',
                      },
                      background: '#f8f9fa',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Title of the Audio
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: '13px',
                        sm: '14px',
                        md: '14px',
                        lg: '15px',
                        xl: '15px',
                      },
                      color: '#1a1a2e',
                      fontWeight: 600,
                      padding: {
                        xs: '12px 8px',
                        sm: '16px 12px',
                        md: '16px 12px',
                        lg: '18px 16px',
                        xl: '18px 16px',
                      },
                      background: '#f8f9fa',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Category
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: '13px',
                        sm: '14px',
                        md: '14px',
                        lg: '15px',
                        xl: '15px',
                      },
                      color: '#1a1a2e',
                      fontWeight: 600,
                      padding: {
                        xs: '12px 8px',
                        sm: '16px 12px',
                        md: '16px 12px',
                        lg: '18px 16px',
                        xl: '18px 16px',
                      },
                      background: '#f8f9fa',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Contests
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: '13px',
                        sm: '14px',
                        md: '14px',
                        lg: '15px',
                        xl: '15px',
                      },
                      color: '#1a1a2e',
                      fontWeight: 600,
                      padding: {
                        xs: '12px 8px',
                        sm: '16px 12px',
                        md: '16px 12px',
                        lg: '18px 16px',
                        xl: '18px 16px',
                      },
                      background: '#f8f9fa',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Challenges
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: {
                        xs: '13px',
                        sm: '14px',
                        md: '14px',
                        lg: '15px',
                        xl: '15px',
                      },
                      color: '#1a1a2e',
                      fontWeight: 600,
                      padding: {
                        xs: '12px 8px',
                        sm: '16px 12px',
                        md: '16px 12px',
                        lg: '18px 16px',
                        xl: '18px 16px',
                      },
                      background: '#f8f9fa',
                      textAlign: 'center',
                      letterSpacing: '0.02em',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedAudios.length ? (
                  displayedAudios.map((audio, index) => (
                    <TableRow 
                      key={audio?._id || index}
                      sx={{
                        backgroundColor: selectedAudioIds.includes(audio._id) ? 'rgba(102, 126, 234, 0.08)' : 'inherit',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: selectedAudioIds.includes(audio._id) 
                            ? 'rgba(102, 126, 234, 0.12)' 
                            : 'rgba(102, 126, 234, 0.04)',
                          transform: 'scale(1.001)',
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: selectedAudioIds.includes(audio._id) 
                            ? 'rgba(102, 126, 234, 0.08)' 
                            : 'rgba(102, 126, 234, 0.02)',
                        },
                      }}
                    >
                      <TableCell sx={{ padding: '10px' }}>
                        <Checkbox
                          checked={selectedAudioIds.includes(audio._id)}
                          onChange={() => handleSelectAudio(audio._id)}
                          sx={{
                            color: '#142C5C',
                            '&.Mui-checked': { color: '#142C5C' },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '13px',
                            md: '14px',
                            lg: '15px',
                            xl: '15px',
                          },
                          color: '#333',
                          padding: {
                            xs: '10px 5px',
                            sm: '10px',
                            md: '10px',
                            lg: '10px 15px',
                            xl: '10px 15px',
                          },
                        }}
                      >
                        <img
                          src={audio?.image || defaultAudioThumbnail}
                          alt={audio?.name}
                          style={{
                            paddingLeft: '20px',
                            height: '50px',
                            width: '50px',
                            objectFit: 'cover',
                            borderRadius: '6px',
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultAudioThumbnail;
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '13px',
                            md: '14px',
                            lg: '15px',
                            xl: '15px',
                          },
                          color: '#333',
                          padding: {
                            xs: '10px 5px',
                            sm: '10px',
                            md: '10px',
                            lg: '10px 15px',
                            xl: '10px 15px',
                          },
                        }}
                      >
                        {audio?.name}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '13px',
                            md: '14px',
                            lg: '15px',
                            xl: '15px',
                          },
                          color: '#333',
                          padding: {
                            xs: '10px 5px',
                            sm: '10px',
                            md: '10px',
                            lg: '10px 15px',
                            xl: '10px 15px',
                          },
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {audio?.categories?.map((category, index) => (
                            <CategoryPill
                              key={`${audio?._id}-${category._id}-${index}`}
                              category={category}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '13px',
                            md: '14px',
                            lg: '15px',
                            xl: '15px',
                          },
                          color: '#333',
                          padding: {
                            xs: '10px 5px',
                            sm: '10px',
                            md: '10px',
                            lg: '10px 15px',
                            xl: '10px 15px',
                          },
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {audio?.contests?.map((contest) => (
                            <ContestPill
                              key={`${audio?._id}-${contest._id}`}
                              contest={contest}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '13px',
                            md: '14px',
                            lg: '15px',
                            xl: '15px',
                          },
                          color: '#333',
                          padding: {
                            xs: '10px 5px',
                            sm: '10px',
                            md: '10px',
                            lg: '10px 15px',
                            xl: '10px 15px',
                          },
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {audio?.challenges?.map((challenge) => (
                            <ChallengePill
                              key={`${audio?._id}-${challenge._id}`}
                              challenge={challenge}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: {
                            xs: '12px',
                            sm: '13px',
                            md: '14px',
                            lg: '15px',
                            xl: '15px',
                          },
                          color: '#333',
                          padding: {
                            xs: '10px 5px',
                            sm: '10px',
                            md: '10px',
                            lg: '10px 15px',
                            xl: '10px 15px',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <ActionFilter
                            onView={() => handleViewAudio(audio)}
                            onEdit={() => handleEditAudio(audio)}
                            onDelete={() => handleDeleteAudio(audio)}
                            index={index}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{
                        height: 'calc(100vh - 210px)',
                        fontSize: {
                          xs: '12px',
                          sm: '13px',
                          md: '14px',
                          lg: '15px',
                          xl: '15px',
                        },
                        color: '#333',
                        padding: {
                          xs: '10px 5px',
                          sm: '10px',
                          md: '10px',
                          lg: '10px 15px',
                          xl: '10px 15px',
                        },
                      }}
                    >
                      {searchQuery || selectedCategoryIds.length > 0 ? 'No matching records found' : 'No records found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Bulk Actions Bar */}
          {selectedAudioIds.length > 0 && (
            <BulkActionsBar
              selectedCount={selectedAudioIds.length}
              onDelete={handleBulkDelete}
              onAssignCategories={handleBulkCategoryAssign}
              onAssignContests={handleBulkContestAssign}
              onClearSelection={() => setSelectedAudioIds([])}
            />
          )}
          {audioModal === 'add' && (
            <AddAudioModal
              openDialog={true}
              handleCloseDialog={closeModalHandler}
              classes={classes}
              categoryTypes={categoryTypes}
              closeModalHandler={closeModalHandler}
              fetchAudios={fetchAudios}
              selectedAudio={''}
              contests={contests}
              challenges={challenges}
            />
          )}
          {audioModal === 'bulk' && (
            <BulkUploadModal
              openDialog={true}
              handleCloseDialog={closeModalHandler}
              classes={classes}
              categoryTypes={categoryTypes}
              fetchAudios={fetchAudios}
              contests={contests}
            />
          )}
          {audioModal === 'delete' && (
            <ConfirmDeleteModal
              onAccept={confirmDeleteHandler}
              onReject={closeModalHandler}
              audio={true}
            />
          )}
          {audioModal === 'view' && (
            <ViewAudioModal
              openDialog={true}
              handleCloseDialog={closeModalHandler}
              classes={classes}
              selectedAudio={selectedAudio}
            />
          )}
          {audioModal === 'edit' && (
            <AddAudioModal
              openDialog={true}
              handleCloseDialog={closeModalHandler}
              classes={classes}
              categoryTypes={categoryTypes}
              selectedAudio={selectedAudio}
              edit={true}
              closeModalHandler={closeModalHandler}
              fetchAudios={fetchAudios}
              contests={contests}
              challenges={challenges}
            />
          )}
          {audioModal === 'bulkDelete' && (
            <ConfirmDeleteModal
              onAccept={confirmBulkDeleteHandler}
              onReject={closeModalHandler}
              audio={true}
              bulkCount={selectedAudioIds.length}
            />
          )}
          {audioModal === 'bulkCategory' && (
            <BulkCategoryModal
              open={true}
              onClose={closeModalHandler}
              categoryTypes={categoryTypes}
              onSubmit={handleBulkCategorySubmit}
              selectedCount={selectedAudioIds.length}
            />
          )}
          {audioModal === 'bulkContest' && (
            <BulkContestModal
              open={true}
              onClose={closeModalHandler}
              contests={contests}
              onSubmit={handleBulkContestSubmit}
              selectedCount={selectedAudioIds.length}
            />
          )}
        </Box>
        <ToastContainer />
      </>
    </PageLayout>
  );
};

export default ManageAudio;
